import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import { ChevronDown, ChevronUp, X } from 'lucide-react'

import api from '../../../services/api'
import { getDepartmentUrl } from '../../../services/functions'
import { formatAgendaAppointment } from '../../../services/formatResponses/agendaInteligente'
import { CALENDAR, SCHEDULING_DEPARTMENT_STATUS_COLOR } from '../../../styles/constant'

import {
  Aviso,
  Body,
  Cabecalho,
  Celula,
  CloseButton,
  ColunaBotao,
  Etiqueta,
  Header,
  HeaderText,
  Hora,
  Linha,
  LinhaTopo,
  Overlay,
  Panel,
  StatusTag,
  Vazio,
} from './styles'

/** Os horarios da agenda vem em segundos a partir da meia-noite. */
function paraHora(segundos) {
  if (segundos === null || segundos === undefined) return ''

  const minutos = Math.floor(Number(segundos) / 60)
  const hora = String(Math.floor(minutos / 60)).padStart(2, '0')
  const minuto = String(minutos % 60).padStart(2, '0')

  return `${hora}:${minuto}`
}

/*
 * Nome e cor do status saem das MESMAS constantes que pintam as bolhas da
 * grade. A etiqueta precisa dizer a mesma coisa que a bolha: cinza para tudo
 * obrigava a loja a traduzir entre as duas, e o nome cru ("ORÇAMENTO
 * ENVIADO") ainda era cortado na coluna, quando a grade ja chama isso de
 * "Finalizado".
 */
function infoDoStatus(status, tema) {
  const chave = String(status || '').toUpperCase()
  const mapaDeNomes = CALENDAR?.status?.[tema === 'draMei' ? 'draMei' : 'petland'] || {}
  const paleta =
    SCHEDULING_DEPARTMENT_STATUS_COLOR[tema === 'draMei' ? 'veterinary' : 'petShop'] || {}

  const entrada = mapaDeNomes[chave] || CALENDAR?.status?.[chave] || null

  return {
    nome: entrada?.name || status || '',
    cor: entrada?.code ? paleta[entrada.code] : null,
  }
}

/*
 * A lista sai do que a agenda JA tem em memoria: e a agenda que sabe de quem e
 * cada agendamento (employeeId), e assim a gaveta abre na hora. O que falta ali
 * e "assinante" e a sugestao de venda, que so existem no smart-schedule.
 */
function linhaBase(agendamento) {
  const servicos =
    Array.isArray(agendamento.services) && agendamento.services.length
      ? agendamento.services
      : agendamento.serviceName
        ? String(agendamento.serviceName).split(' | ')
        : []

  return {
    id: agendamento.id,
    // chaves possiveis do mesmo agendamento, da mais exata para a mais frouxa
    chaves: [
      agendamento.appointmentOrderId,
      agendamento.appointmentId,
      agendamento.serviceOrderId,
      agendamento.id,
    ]
      .filter(Boolean)
      .map(String),
    inicio: Number(agendamento.startsAt) || 0,
    hora: `${paraHora(agendamento.startsAt)}–${paraHora(agendamento.finishesAt)}`,
    cliente: agendamento.customerName || 'Não informado',
    /*
     * So o primeiro nome do tutor na coluna. O nome completo cortado em
     * "Leonardo Barb…" nao identifica ninguem melhor que "Leonardo" e ainda
     * rouba a largura do Servico. O nome inteiro continua no title (e e ele
     * que a ordenacao usa), entao nada se perde ao passar o mouse.
     */
    clienteCurto: String(agendamento.customerName || 'Não informado').trim().split(/\s+/)[0],
    pet: agendamento.petName || 'Não informado',
    servicos,
    servico: servicos.join(', '),
    status: agendamento.status || '',
  }
}

const COLUNAS = [
  { chave: 'hora', rotulo: 'Horário', campo: 'inicio', tipo: 'numero' },
  { chave: 'cliente', rotulo: 'Cliente', campo: 'cliente', tipo: 'texto' },
  { chave: 'pet', rotulo: 'Pet', campo: 'pet', tipo: 'texto' },
  { chave: 'servico', rotulo: 'Serviço', campo: 'servico', tipo: 'texto' },
  { chave: 'assinante', rotulo: 'Assinante', campo: 'assinante', tipo: 'booleano' },
  { chave: 'status', rotulo: 'Status', campo: 'status', tipo: 'texto' },
]

/*
 * Mesma gaveta para dois caminhos: o numero do profissional no cabecalho e os
 * numeros do informativo do dia. Os dois querem a mesma coisa — "me mostra
 * quais agendamentos compoem esse numero" — entao recebem a lista ja filtrada
 * e so mudam o titulo. Duas gavetas parecidas divergiriam na primeira
 * correcao feita so numa delas.
 */
export function AppointmentsDrawer({
  titulo,
  date,
  appointments,
  theme,
  onClose,
}) {
  const [enriquecidos, setEnriquecidos] = useState(null)
  const [falhouEnriquecer, setFalhouEnriquecer] = useState(false)
  const [ordem, setOrdem] = useState({ campo: 'inicio', direcao: 'asc' })

  useEffect(() => {
    const fechaNoEsc = (evento) => {
      if (evento.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', fechaNoEsc)
    return () => document.removeEventListener('keydown', fechaNoEsc)
  }, [onClose])

  useEffect(() => {
    let cancelado = false

    async function buscaDetalhes() {
      try {
        const departamento = getDepartmentUrl()
        const { data } = await api.get('smart-schedule/appointments', {
          params: { date, department: departamento },
          timeout: 8000,
        })

        const lista = Array.isArray(data) ? data : data?.appointments || data?.results || []
        const formatados = lista.map((item) =>
          formatAgendaAppointment(item, { date, department: departamento })
        )

        /*
         * Junta por appointmentOrderId.
         *
         * A agenda tem uma bolha por SERVICO (um serviceOrder cada) e o
         * smart-schedule tem uma linha por AGENDAMENTO, agrupando os servicos
         * do mesmo pet no mesmo horario. Sao granularidades diferentes: juntar
         * pelo serviceOrderId nao achava nada, e por isso assinante vinha "—"
         * e o historico nunca abria. appointmentOrderId existe nos dois lados,
         * entao um agendamento de tres servicos enriquece as tres bolhas.
         */
        const porId = {}
        formatados.forEach((item) => {
          ;[item.appointmentOrderId, item.appointmentId]
            .filter(Boolean)
            .forEach((chave) => {
              porId[String(chave)] = item
            })
        })

        if (!cancelado) setEnriquecidos(porId)
      } catch (erro) {
        console.error('Nao foi possivel carregar assinante e sugestoes:', erro)
        if (!cancelado) setFalhouEnriquecer(true)
      }
    }

    buscaDetalhes()
    return () => {
      cancelado = true
    }
  }, [date])

  const linhas = useMemo(() => {
    const base = [...(appointments || [])].map(linhaBase)

    const comDetalhe = base.map((linha) => {
      const rico = linha.chaves.reduce(
        (achado, chave) => achado || enriquecidos?.[chave] || null,
        null
      )
      return { ...linha, rico, assinante: rico ? !!rico.assinante : null }
    })

    const coluna = COLUNAS.find((item) => item.campo === ordem.campo)
    const sinal = ordem.direcao === 'desc' ? -1 : 1

    return comDetalhe.sort((a, b) => {
      const x = a[ordem.campo]
      const y = b[ordem.campo]

      // vazio e "ainda nao sei" vao sempre para o fim, nas duas direcoes
      const aVazio = x === null || x === undefined || x === ''
      const bVazio = y === null || y === undefined || y === ''
      if (aVazio && bVazio) return a.inicio - b.inicio
      if (aVazio) return 1
      if (bVazio) return -1

      if (coluna?.tipo === 'texto') return sinal * String(x).localeCompare(String(y), 'pt-BR')
      return sinal * (Number(x) - Number(y))
    })
  }, [appointments, enriquecidos, ordem])

  const ordenaPor = (campo) =>
    setOrdem((atual) =>
      atual.campo === campo
        ? { campo, direcao: atual.direcao === 'asc' ? 'desc' : 'asc' }
        : { campo, direcao: 'asc' }
    )

  const total = linhas.length

  /*
   * Portal para o body: a gaveta e position:fixed, e um ancestral com
   * transform faria "fixed" valer em relacao a esse ancestral em vez da
   * janela — foi assim que o menu de acoes da tabela ficou preso na linha.
   */
  return createPortal(
    <>
      <Overlay onClick={onClose} />
      <Panel role="dialog" aria-label={`Agendamentos: ${titulo || ''}`}>
        <Header>
          <HeaderText>
            <h2>{titulo}</h2>
            <p>
              {moment(date).format('DD/MM/YY')} · {total}{' '}
              {total === 1 ? 'agendamento' : 'agendamentos'}
            </p>
          </HeaderText>
          <CloseButton type="button" onClick={onClose} title="Fechar" aria-label="Fechar">
            <X size={18} />
          </CloseButton>
        </Header>

        <Body>
          {falhouEnriquecer && (
            <Aviso>
              Não consegui carregar “assinante” e as sugestões de venda agora. A lista
              abaixo está completa; só essas duas informações ficaram de fora.
            </Aviso>
          )}

          {total === 0 ? (
            <Vazio>Nenhum agendamento para este profissional no dia.</Vazio>
          ) : (
            <Cabecalho>
              {COLUNAS.map((coluna) => (
                <ColunaBotao
                  key={coluna.chave}
                  type="button"
                  $ativa={ordem.campo === coluna.campo}
                  onClick={() => ordenaPor(coluna.campo)}
                  title={`Ordenar por ${coluna.rotulo}`}
                >
                  {coluna.rotulo}
                  {ordem.campo === coluna.campo &&
                    (ordem.direcao === 'asc' ? (
                      <ChevronUp size={12} />
                    ) : (
                      <ChevronDown size={12} />
                    ))}
                </ColunaBotao>
              ))}
            </Cabecalho>
          )}

          {linhas.map((linha) => {
            const status = infoDoStatus(linha.status, theme)

            return (
              <Linha key={linha.id}>
                <LinhaTopo>
                  <Hora>{linha.hora}</Hora>
                  <Celula title={linha.cliente}>
                    <strong>{linha.clienteCurto}</strong>
                  </Celula>
                  <Celula title={linha.pet}>{linha.pet}</Celula>
                  <Celula title={linha.servico}>{linha.servico}</Celula>
                  {/* sem a busca de detalhes nao da para afirmar nem sim nem nao */}
                  <Etiqueta $sim={linha.assinante}>
                    {linha.assinante === null ? '—' : linha.assinante ? 'Assinante' : 'Não'}
                  </Etiqueta>
                  <StatusTag title={status.nome} $cor={status.cor}>
                    {status.nome}
                  </StatusTag>
                </LinhaTopo>
              </Linha>
            )
          })}
        </Body>
      </Panel>
    </>,
    document.body
  )
}
